import {clsx} from "clsx";

type ButtonVariant = "primary" | "secondary" | "blackBtn" | "greyBtn" | "whiteBtn" | "whiteBtnDefault" | "transparent" | "outline" | "outlinePrimary" | "danger" | "outlineDanger" | 
"outlineSecondary" | "blackBtnDefault" | "whiteBtnDefault" | "blueBtnDefault";

type ButtonSize = "sm" | "md" | "lg";

type CommonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  iconOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
};

type ButtonAsButton = CommonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: "button";
  };

type ButtonAsLink = CommonProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    as: "a";
  };

type ButtonProps = ButtonAsButton | ButtonAsLink;

const baseStyles =
  "inline-flex items-center justify-center gap-2 transition-colors focus:outline-none disabled:opacity-50 disabled:pointer-events-none";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-primary text-white hover:bg-primary",
  secondary: "bg-[#242424] text-white border border-transparent",
  blackBtn: "bg-black text-white !border border-primary",
  greyBtn: "bg-[#EFEFEF] text-[#7F7F7F] !border border-transparent",
  whiteBtn: "bg-white !text-black border border-primary",
  transparent: "bg-transparent text-white hover:bg-white/5",
  outline: "bg-transparent !border !border-primary text-primary hover:bg-primary/10",
  outlinePrimary: "bg-transparent !border border-primary text-primary hover:bg-primary hover:text-white transition-colors",
  danger: "bg-[#DA291C] text-white hover:bg-red-700 border border-[#DA291C]",
  outlineDanger: "text-[#DA291C] !border border-[#DA291C]  hover:bg-red-700 hover:text-white ",
  outlineSecondary: "bg-white text-[#7F7F7F] !border border-[#7F7F7F]",
  blackBtnDefault: "bg-black text-white border border-black",
  whiteBtnDefault: "bg-white text-black !border border-grey1",
  blueBtnDefault: "bg-bgBlue text-white !border border-bgBlue",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-sm",
  md: "px-6 py-2 text-sm",
  lg: "h-12 px-9 text-base",
};

const iconOnlySizes: Record<ButtonSize, string> = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

const Button = ({
  as = "button",
  variant = "primary",
  size = "md",
  leftIcon,
  rightIcon,
  iconOnly,
  loading,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) => {
  const Comp: any = as;

  return (
    <Comp
      className={clsx(
        baseStyles,
        variants[variant],
        iconOnly ? iconOnlySizes[size] : sizes[size],
        iconOnly && "p-0",
        className
      )}
      disabled={as === "button" ? disabled || loading : undefined}
      aria-busy={loading}
      {...props}
    >
      {loading ? (
        <span className="h-4 w-4 border-2 border-white/40 border-t-white" />
      ) : (
        <>
          {leftIcon && <span >{leftIcon}</span>}
          {!iconOnly && <span className="whitespace-nowrap">{children}</span>}
          {rightIcon && <span>{rightIcon}</span>}
        </>
      )}
    </Comp>
  );
};

export default Button;
